import { useState } from 'react';
import { BookOpen, CalendarPlus } from 'lucide-react';
import { Button, Card, CardBody, EmptyState, ErrorState, LoadingState, PageHeader, cn } from '@tutor/ui';
import { useLessons, type LessonFilters } from '../../hooks/queries';
import { LessonCard } from '../../components/LessonCard';
import { ScheduleLessonDialog } from '../../components/dialogs/ScheduleLessonDialog';

const SCOPES = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'today', label: 'Today' },
  { value: 'past', label: 'Past' },
  { value: 'all', label: 'All' },
] as const;

export function LessonsPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const [scope, setScope] = useState<LessonFilters['scope']>('upcoming');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const { data, isLoading, isError } = useLessons({ scope, pageSize: 50 });

  return (
    <>
      <PageHeader
        title="Lessons"
        description={
          perspective === 'tutor'
            ? 'Every lesson you are teaching.'
            : 'Every lesson on your timetable.'
        }
        action={
          perspective === 'tutor' ? (
            <Button
              onClick={() => setScheduleOpen(true)}
              leftIcon={<CalendarPlus className="h-4 w-4" aria-hidden />}
            >
              Schedule lesson
            </Button>
          ) : undefined
        }
      />

      {perspective === 'tutor' && (
        <ScheduleLessonDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} />
      )}

      <div className="mb-4 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        {SCOPES.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setScope(option.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              scope === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Card>
        <CardBody className="space-y-3">
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <ErrorState description="We could not load your lessons." />
          ) : (data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="No lessons here"
              description={
                scope === 'upcoming'
                  ? 'Nothing scheduled yet.'
                  : 'Try a different filter to see more.'
              }
              icon={<BookOpen className="h-5 w-5" aria-hidden />}
            />
          ) : (
            data!.items.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} perspective={perspective} showDay />
            ))
          )}
        </CardBody>
      </Card>
    </>
  );
}
